import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, serverTimestamp, addDoc, writeBatch } from 'firebase/firestore';
import { useAuth } from '../contexts/authContext';
import { BookOpenIcon, CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { loadContentTrackProgress } from '../utils/contentTrackProgress';

const markResourceAsCompleted = async (discipleId, mentorAppID, resourceId) => {
  try {
    const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorAppID);
    const mentorDetailsDoc = await getDoc(mentorDetailsRef);
    const existingProgress = mentorDetailsDoc.data()?.contentTrackProgress;

    if (!existingProgress) {
      console.warn('No content track progress found. Cannot mark resource as completed.');
      return null;
    }

    const completedResourceIds = existingProgress.completedResourceIds || [];

    if (completedResourceIds.includes(resourceId)) {
      console.log('Resource already marked as completed:', resourceId);
      return existingProgress;
    }

    const updatedCompletedResourceIds = [...completedResourceIds, resourceId];

    const updatedProgress = {
      ...existingProgress,
      completedResourceIds: updatedCompletedResourceIds,
      lastUpdated: serverTimestamp(),
    };

    await updateDoc(mentorDetailsRef, {
      'contentTrackProgress': updatedProgress,
    });

    return updatedProgress;
  } catch (error) {
    console.error('Error marking resource as completed:', error);
    return null;
  }
};

const ReadTogether = () => {
  const { discipleId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [disciple, setDisciple] = useState(null);
  const [contentTrack, setContentTrack] = useState(null);
  const [contentResources, setContentResources] = useState([]);
  const [progress, setProgress] = useState(null);
  const [currentResource, setCurrentResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markingComplete, setMarkingComplete] = useState(false);

  // Update page title when disciple name is loaded
  useEffect(() => {
    if (disciple?.name) {
      document.title = `Read with ${disciple.name} - Together On Mission`;
    }
  }, [disciple?.name]);

  useEffect(() => {
    loadData();
  }, [discipleId]);

  const loadData = async () => {
    if (!currentUser || !discipleId) return;

    try {
      setLoading(true);

      // Load disciple
      const discipleDoc = await getDoc(doc(db, 'disciples', discipleId));
      if (!discipleDoc.exists()) {
        alert('Disciple not found');
        navigate(-1);
        return;
      }

      const discipleData = { id: discipleDoc.id, ...discipleDoc.data() };
      setDisciple(discipleData);

      // Load mentor details to get subscribed track
      const mentorAppID = currentUser.appID || currentUser.uid;
      const mentorDetailsDoc = await getDoc(
        doc(db, 'disciples', discipleId, 'mentorDetails', mentorAppID)
      );

      if (!mentorDetailsDoc.exists()) {
        alert('Mentor details not found');
        navigate(-1);
        return;
      }

      const trackId = mentorDetailsDoc.data()?.subscribedContentTrackId;
      if (!trackId) {
        alert('This disciple is not subscribed to a content track.');
        navigate(-1);
        return;
      }

      // Load track details
      const trackDoc = await getDoc(doc(db, 'contentTracks', trackId));

      if (!trackDoc.exists()) {
        alert('Content track not found');
        navigate(-1);
        return;
      }

      const trackData = { id: trackDoc.id, ...trackDoc.data() };
      setContentTrack(trackData);

      // Verify it's a children's track
      if (trackData.trackType !== 'children') {
        alert('This feature is only available for children\'s content tracks.');
        navigate(-1);
        return;
      }

      // Load progress
      const progressData = await loadContentTrackProgress(trackId, discipleId, mentorAppID);
      if (progressData) {
        setProgress(progressData);
      }

      // Load resources from track
      const resourcesSnapshot = await getDocs(
        collection(db, 'contentTracks', trackId, 'resources')
      );

      // Filter for published resources and sort by displayOrder
      const resources = resourcesSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(resource => resource.status === 'published' || !resource.status)
        .sort((a, b) => {
          const orderA = a.displayOrder ?? 999;
          const orderB = b.displayOrder ?? 999;
          return orderA - orderB;
        });

      setContentResources(resources);

      // Find next uncompleted resource
      const completedIds = progressData?.completedResourceIds || [];
      const nextUncompleted = resources.find(r => !completedIds.includes(r.id));

      if (nextUncompleted) {
        setCurrentResource(nextUncompleted);
      } else if (resources.length > 0) {
        // All completed, show last one
        setCurrentResource(resources[resources.length - 1]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  // Archive all active reminders for a specific disciple (same as SendContentMessage.js)
  const archiveRemindersForDisciple = async (userId, discipleId) => {
    try {
      const remindersQuery = query(
        collection(db, 'users', userId, 'reminderQueue'),
        where('discipleId', '==', discipleId),
        where('archived', '==', false),
        where('reminderStatus', 'in', ['sent', 'snoozed'])
      );

      const remindersSnapshot = await getDocs(remindersQuery);

      if (remindersSnapshot.empty) {
        console.log(`No active reminders to archive for disciple ${discipleId}`);
        return;
      }

      const batch = writeBatch(db);
      remindersSnapshot.docs.forEach((docRef) => {
        batch.update(docRef.ref, {
          archived: true,
          reminderStatus: 'completed',
          completedAt: serverTimestamp(),
        });
      });

      await batch.commit();
      console.log(`Archived ${remindersSnapshot.docs.length} reminders for disciple ${discipleId}`);
    } catch (error) {
      console.error('Error archiving reminders for disciple:', error);
      // Don't throw - reminder archiving is not critical
    }
  };

  // Log content completion to history (same pattern as SendContentMessage.js)
  const logContentCompletionToHistory = async (mentorAppID, resourceId, resourceTitle) => {
    try {
      await addDoc(
        collection(db, 'disciples', discipleId, 'mentorDetails', mentorAppID, 'history'),
        {
          type: 'content',
          resourceId: resourceId,
          resourceTitle: resourceTitle,
          action: 'read',
          contentTrackName: contentTrack?.name || null,
          createdAt: serverTimestamp(),
        }
      );
      console.log('Logged content completion to history');
    } catch (error) {
      console.error('Error logging to history:', error);
      // Don't throw - history logging is not critical
    }
  };

  const handleFinished = async () => {
    if (!currentResource || !progress || markingComplete) return;

    try {
      setMarkingComplete(true);

      const mentorAppID = currentUser?.appID || currentUser?.uid;
      if (!mentorAppID) return;

      // Mark resource as completed
      const updatedProgress = await markResourceAsCompleted(
        discipleId,
        mentorAppID,
        currentResource.id
      );

      if (updatedProgress) {
        setProgress(updatedProgress);

        // Log to history
        try {
          await logContentCompletionToHistory(
            mentorAppID,
            currentResource.id,
            currentResource.title
          );
        } catch (error) {
          console.error('Error logging to history:', error);
          // Continue even if history logging fails
        }

        // Archive reminders for this disciple
        if (currentUser?.uid) {
          try {
            await archiveRemindersForDisciple(currentUser.uid, discipleId);
          } catch (error) {
            console.error('Error archiving reminders:', error);
            // Continue even if reminder archiving fails
          }
        }

        // Update lastInteractionAt in mentorDetails
        try {
          const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', mentorAppID);
          await updateDoc(mentorDetailsRef, {
            lastInteractionAt: serverTimestamp(),
          });
        } catch (error) {
          console.error('Error updating lastInteractionAt:', error);
          // Continue even if update fails
        }

        // Automatically advance to next uncompleted resource
        const completedIds = updatedProgress.completedResourceIds || [];
        const nextUncompleted = contentResources.find(r => !completedIds.includes(r.id));

        if (nextUncompleted) {
          // Advance to next story
          setCurrentResource(nextUncompleted);
        } else {
          // All completed!
          alert(
            `You've completed all ${contentResources.length} stories with ${disciple?.name || 'your disciple'}!`
          );
        }
      } else {
        alert('Failed to mark resource as completed');
      }
    } catch (error) {
      console.error('Error marking as finished:', error);
      alert('Failed to mark resource as completed');
    } finally {
      setMarkingComplete(false);
    }
  };

  const handlePrevious = () => {
    if (!currentResource || contentResources.length === 0) return;
    
    const currentIndex = contentResources.findIndex(r => r.id === currentResource.id);
    if (currentIndex > 0) {
      setCurrentResource(contentResources[currentIndex - 1]);
      // Scroll to top when navigating
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleNext = () => {
    if (!currentResource || contentResources.length === 0) return;
    
    const currentIndex = contentResources.findIndex(r => r.id === currentResource.id);
    if (currentIndex < contentResources.length - 1) {
      setCurrentResource(contentResources[currentIndex + 1]);
      // Scroll to top when navigating
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getCurrentResourceIndex = () => {
    if (!currentResource) return 0;
    return contentResources.findIndex(r => r.id === currentResource.id) + 1;
  };

  const getCompletedCount = () => {
    return progress?.completedResourceIds?.length || 0;
  };

  const isAllCompleted = () => {
    if (!progress || contentResources.length === 0) return false;
    const completedIds = progress.completedResourceIds || [];
    return completedIds.length >= contentResources.length;
  };

  const renderBibleStory = (resource) => {
    // Support both 'content' (new format) and 'text' (old format) fields
    const storyContent = resource.content || resource.text || '';
    const authorName = resource.authorName || resource.author || '';
    const authorWebsite = resource.authorWebsite || resource.website || '';
    
    return (
      <div className="bg-white">
        <div className="px-6 pt-6">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{resource.title}</h2>
          
          {/* Author and Website - microtext */}
          {(authorName || authorWebsite) && (
            <div className="mb-4 pb-3 border-b border-gray-200">
              <p className="text-xs text-gray-400">
                {authorName && <span>{authorName}</span>}
                {authorName && authorWebsite && <span className="mx-1.5">·</span>}
                {authorWebsite && (
                  <a 
                    href={authorWebsite} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-500 transition-colors no-underline"
                  >
                    {authorWebsite.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </p>
            </div>
          )}
          
          {resource.subtitle && (
            <h3 className="text-xl font-medium text-gray-600 mb-6 italic">{resource.subtitle}</h3>
          )}
          {storyContent && (
            <div className="text-base md:text-lg text-gray-800 leading-relaxed whitespace-pre-wrap mb-6">
              {storyContent.split('\n').map((paragraph, index) => (
                paragraph.trim() ? (
                  <p key={index} className="mb-4">{paragraph}</p>
                ) : (
                  <br key={index} />
                )
              ))}
            </div>
          )}
          {resource.verses && resource.verses.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-600 mb-2">Bible Verses:</h4>
              {resource.verses.map((verse, index) => (
                <p key={index} className="text-base text-gray-800 italic mb-2">{verse}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderMemoryVerse = (resource) => {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{resource.title}</h2>
        {resource.verseReference && (
          <div className="flex items-center gap-2 mb-4">
            <BookOpenIcon className="h-5 w-5 text-blue-600" />
            <span className="text-lg font-semibold text-blue-600">{resource.verseReference}</span>
          </div>
        )}
        {resource.text && (
          <div className="bg-blue-50 p-6 rounded-lg mb-4">
            <p className="text-lg text-gray-800 text-center leading-relaxed font-medium">
              {resource.text}
            </p>
          </div>
        )}
        {resource.memoryVerseText && (
          <div className="border-l-4 border-blue-600 pl-4 py-2 bg-gray-50 rounded">
            <h4 className="text-sm font-semibold text-gray-600 mb-2">Note:</h4>
            <p className="text-base text-gray-700">{resource.memoryVerseText}</p>
          </div>
        )}
      </div>
    );
  };

  const renderBibleStoryVideo = (resource) => {
    const videoUrl = resource.url || resource.videoUrl;

    return (
      <div className="bg-white rounded-lg shadow">
        {videoUrl && (
          <div className="w-full bg-black rounded-t-lg" style={{ aspectRatio: '16/9' }}>
            <video
              src={videoUrl}
              controls
              className="w-full h-full"
              style={{ maxHeight: '500px' }}
            />
          </div>
        )}
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{resource.title}</h2>
          {resource.subtitle && (
            <h3 className="text-xl font-medium text-gray-600 mb-4">{resource.subtitle}</h3>
          )}
          {resource.text && (
            <p className="text-base text-gray-700 mb-6 leading-relaxed whitespace-pre-wrap">
              {resource.text}
            </p>
          )}
          {resource.verses && resource.verses.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-600 mb-2">Bible Verses:</h4>
              {resource.verses.map((verse, index) => (
                <p key={index} className="text-base text-gray-800 italic mb-2">{verse}</p>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!currentResource) return null;

    const format = currentResource.format || 'bible-story';

    switch (format) {
      case 'bible-story':
        return renderBibleStory(currentResource);
      case 'memory-verse':
        return renderMemoryVerse(currentResource);
      case 'bible-story-video':
        return renderBibleStoryVideo(currentResource);
      default:
        return renderBibleStory(currentResource);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (!disciple || !contentTrack || !currentResource) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Content Available</h3>
          <p className="text-gray-600">
            {contentResources.length === 0
              ? 'This content track has no published resources yet.'
              : 'All content has been completed!'}
          </p>
        </div>
      </div>
    );
  }

  const currentIndex = getCurrentResourceIndex();
  const completedCount = getCompletedCount();
  const isCompleted = progress?.completedResourceIds?.includes(currentResource.id) || false;
  const progressPercentage = (completedCount / contentResources.length) * 100;

  return (
    <div className="h-screen flex flex-col bg-white overflow-hidden">
      {/* Fixed Image at Top - 34% of screen height with 2px padding */}
      {currentResource?.imageURL && (
        <div className="w-full bg-gray-100 flex items-center justify-center" style={{ height: '34vh', padding: '2px 0' }}>
          <img
            src={currentResource.imageURL}
            alt={currentResource.title}
            className="w-full h-full object-contain"
          />
        </div>
      )}

      {/* Scrollable Content Area - Bottom 66% */}
      <div className="flex-1 overflow-y-auto bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6">
          {renderContent()}

          {/* Finished Button */}
          <div className="mt-6">
            <button
              onClick={handleFinished}
              disabled={markingComplete}
              className="w-full flex items-center justify-center gap-2 bg-[#0076BE] hover:bg-[#0065a3] text-white font-semibold py-4 px-6 rounded-lg transition disabled:opacity-50 text-lg"
            >
              {markingComplete ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Marking...</span>
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-5 w-5" />
                  <span>Finished</span>
                </>
              )}
            </button>
          </div>

          {/* Navigation Buttons - Previous/Next */}
          <div className="mt-4 pb-6 flex gap-3">
            <button
              onClick={handlePrevious}
              disabled={getCurrentResourceIndex() <= 1}
              className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed text-gray-700 font-semibold py-3 px-4 rounded-lg transition"
            >
              <ChevronLeftIcon className="h-5 w-5" />
              <span>Previous</span>
            </button>
            <button
              onClick={handleNext}
              disabled={getCurrentResourceIndex() >= contentResources.length}
              className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed text-gray-700 font-semibold py-3 px-4 rounded-lg transition"
            >
              <span>Next</span>
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReadTogether;
